
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import type { RaceEvent, RaceCategory, TrackPoint, OfficialResult } from '../types';
import { parseAndAddAthlete } from '../services/dataParser';
import { usePlayback } from '../hooks/usePlayback';
import Header from '../components/Header';
import RaceMap from '../components/RaceMap';
import PlaybackControls from '../components/PlaybackControls';
import DataChart from '../components/DataChart';
import LapData from '../components/LapData';
import AthleteList, { AthletePerformance } from '../components/AthleteList';
import OfficialResults from '../components/OfficialResults';
import FileUpload from '../components/FileUpload';
import AthleteProfile from '../components/AthleteProfile';
import { MapPinIcon, ArrowsPointingOutIcon, TrashIcon } from '../components/icons/PlaybackIcons';
import RaceLapStats from '../components/RaceLapStats';
import GeminiRaceInsights from '../components/GeminiRaceInsights';
import { getLapCountAtTime, calculateCourseLength, calculateCourseCumulativeDistances, getProjectedPathDistance, calculateVirtualRaceDistance, extractSingleCircuitLoop } from '../services/raceAnalysis';
import type { LapCrossing, OffsetAdjustment, RaceIncident } from '../services/raceAnalysis';
import DraftingStats from '../components/DraftingStats';
import GeminiAthleteInsights from '../components/GeminiAthleteInsights';
import { raceEventService } from '../services/raceService';
import { useAuth } from '../contexts/AuthContext';
import ConfirmationModal from '../components/ConfirmationModal';
import { cloudAnalysisService } from '../services/cloudAnalysisService';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { bikedataService } from '../services/bikedataService';
import { userService } from '../services/userService';
import SEO from '../components/SEO';
import RaceSectorResults from '../components/RaceSectorResults';
import { getSectorPathSlice, computeSectorLeaderboard, computeSectorStats, normalizeSector } from '../utils/raceSector';
import { trimRouteSegment, getDistanceMeters } from '../utils/routeTrimmer';

// Helper to interpolate between two track points based on time
const interpolateTrackPoint = (p1: TrackPoint, p2: TrackPoint, time: number): TrackPoint => {
  const timeDiff = p2.timestamp - p1.timestamp;
  if (timeDiff <= 0) return p1;

  const ratio = (time - p1.timestamp) / timeDiff;

  const interpolateNullable = (v1: number | null, v2: number | null) => {
      if (v1 === null || v2 === null) return null;
      return v1 + (v2 - v1) * ratio;
  }

  return {
    lat: p1.lat + (p2.lat - p1.lat) * ratio,
    lon: p1.lon + (p2.lon - p1.lon) * ratio,
    elevation: p1.elevation + (p2.elevation - p1.elevation) * ratio,
    timestamp: time,
    distance: p1.distance + (p2.distance - p1.distance) * ratio,
    speed: p1.speed + (p2.speed - p1.speed) * ratio,
    power: interpolateNullable(p1.power, p2.power),
    heartRate: interpolateNullable(p1.heartRate, p2.heartRate),
  };
};

// Helper to find the current track point for an athlete at a given time
const getCurrentTrackPointForAthlete = (track: TrackPoint[], time: number): TrackPoint => {
  if (!track.length) {
    return { lat: 0, lon: 0, elevation: 0, timestamp: 0, distance: 0, speed: 0, power: null, heartRate: null };
  }
  
  if (time <= track[0].timestamp) return track[0];
  if (time >= track[track.length - 1].timestamp) return track[track.length - 1];

  // Use a binary search for performance with large track data
  let low = 0;
  let high = track.length - 1;
  while (low <= high) {
      const mid = Math.floor((low + high) / 2);
      if (track[mid].timestamp < time) {
          low = mid + 1;
      } else if (track[mid].timestamp > time) {
          high = mid - 1;
      } else {
          return track[mid];
      }
  }

  if (low > 0 && low < track.length) {
      const p1 = track[low-1];
      const p2 = track[low];
      return interpolateTrackPoint(p1, p2, time);
  }

  return track[track.length - 1];
};

// Helper to fuzzy match athlete names (e.g. "Chirs Barr" to "Christopher Barr")
const fuzzyMatchName = (name1: string, name2: string): boolean => {
    if (!name1 || !name2) return false;
    
    // Normalize: remove punctuation, extra spaces, and lowercase
    const normalize = (n: string) => n.toLowerCase().replace(/[^a-z]/g, ' ').replace(/\s+/g, ' ').trim();
    
    const n1 = normalize(name1);
    const n2 = normalize(name2);
    
    if (n1 === n2) return true;
    
    const tokens1 = n1.split(' ').filter(Boolean);
    const tokens2 = n2.split(' ').filter(Boolean);
    
    // Sort tokens to ignore order (e.g. "Barr Christopher" vs "Christopher Barr")
    if (tokens1.slice().sort().join(' ') === tokens2.slice().sort().join(' ')) return true;

    // Check for shared significant words (e.g., shared last name)
    // We use > 2 to ensure we don't accidentally match on just a shared middle initial
    const sharedWords = tokens1.filter(t => t.length > 2 && tokens2.includes(t));
    
    if (sharedWords.length > 0) {
        // Find the differing parts (the first names/initials)
        const diff1 = tokens1.filter(t => !sharedWords.includes(t)).join('');
        const diff2 = tokens2.filter(t => !sharedWords.includes(t)).join('');
        
        // If the differing parts start with the same letter (e.g. "chirs" and "christopher")
        // Or if one diff is empty (e.g. "Barr" vs "C Barr")
        if ((diff1 && diff2 && diff1[0] === diff2[0]) || (!diff1 || !diff2)) {
            return true;
        }
    }
    
    // Fallback for simple subset matches
    return n1.includes(n2) || n2.includes(n1);
};

const RaceVisualizer: React.FC = () => {
  const { raceId, categoryId } = useParams<{ raceId: string; categoryId: string }>();
  const navigate = useNavigate();
  const { currentUser, isAdmin, userProfile } = useAuth();
  const [raceEvent, setRaceEvent] = useState<RaceEvent | null>(null);
  const [highlightedAthleteId, setHighlightedAthleteId] = useState<string | null>(null);
  const [spotlightedAthleteId, setSpotlightedAthleteId] = useState<string | null>(null);
  const [viewedAthleteId, setViewedAthleteId] = useState<string | null>(null);
  const [insightAthleteId, setInsightAthleteId] = useState<string | null>(null);
  const [activeSidebarTab, setActiveSidebarTab] = useState<'telemetry' | 'official'>('telemetry');

  const [chartMetric, setChartMetric] = useState<'speed' | 'power' | 'heartRate'>('speed');
  const [xAxisMetric, setXAxisMetric] = useState<'time' | 'distance'>('time');
  const [showElevation, setShowElevation] = useState(true);
  const [activeAnalysisTab, setActiveAnalysisTab] = useState<'chart' | 'laps' | 'raceLaps' | 'aiInsights'>('chart');
  const [uploadError, setUploadError] = useState<string | null>(null);

  const [mapView, setMapView] = useState<'full' | 'follow'>('full');
  const [athleteToDeleteId, setAthleteToDeleteId] = useState<string | null>(null);

  // New State for Cloud Analysis
  const [dynamicOffsets, setDynamicOffsets] = useState<Map<string, OffsetAdjustment[]>>(new Map());
  const [incidents, setIncidents] = useState<Map<string, RaceIncident[]>>(new Map());
  const [raceLapsData, setRaceLapsData] = useState<Record<number, LapCrossing[]>>({});
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const [officialResults, setOfficialResults] = useState<OfficialResult[]>([]);
  const [isLoadingResults, setIsLoadingResults] = useState(false);

  // Fetch the full RaceEvent (parent + all categories with rehydrated tracks)
  useEffect(() => {
    if (!raceId) {
      setRaceEvent(null);
      return;
    }

    const fetchRace = async () => {
        try {
            const event = await raceEventService.getRaceEventBySlug(raceId);
            if (event) {
                // If this is a group ride occurrence and its parent series has the master write-up or race sector
                if (event.seriesId && (!event.groupRideDetails?.infoText || !event.groupRideDetails?.spiritedSegment)) {
                    try {
                        const parentSeries = await raceEventService.getRaceEventBySlug(event.seriesId);
                        if (parentSeries?.groupRideDetails) {
                            event.groupRideDetails = {
                                ...parentSeries.groupRideDetails,
                                ...event.groupRideDetails,
                                infoText: event.groupRideDetails?.infoText || parentSeries.groupRideDetails.infoText,
                                spiritedSegment: event.groupRideDetails?.spiritedSegment || parentSeries.groupRideDetails.spiritedSegment,
                            };
                        }
                    } catch (err) {
                        console.warn("Could not load parent series details for group ride", err);
                    }
                }

                setRaceEvent(event);

                // Determine canonical paths (prefer slugs if available)
                const currentRacePath = event.slug || event.id;
                const activeCat = categoryId
                    ? (event.categories.find(c => c.id === categoryId || c.slug === categoryId) || event.categories[0])
                    : event.categories[0];
                const currentCategoryPath = activeCat ? (activeCat.slug || activeCat.id) : '';

                // Canonical redirect: if they visited via ID but slugs exist, or if they didn't specify category
                if (activeCat && (raceId !== currentRacePath || categoryId !== currentCategoryPath)) {
                    navigate(`/race/${currentRacePath}/category/${currentCategoryPath}`, { replace: true });
                }
                return;
            }

            // Legacy URL fallback: /race/{oldCategoryDocId} (pre-hierarchy migration).
            // Old category doc IDs are preserved on the new category subdoc as `_originalDocId`.
            const legacy = await raceEventService.findParentByLegacyCategoryId(raceId);
            if (legacy) {
                navigate(`/race/${legacy.parentId}/category/${legacy.categoryId}`, { replace: true });
                return;
            }

            setRaceEvent(null);
        } catch (e) {
            console.error("Error fetching race event", e);
        }
    }
    fetchRace();
  }, [raceId, categoryId]);

  // Pick the active category from the loaded event
  const activeCategory = useMemo(() => {
      if (!raceEvent || raceEvent.categories.length === 0) return null;
      if (categoryId) {
          return raceEvent.categories.find(c => c.id === categoryId || c.slug === categoryId) || raceEvent.categories[0];
      }
      return raceEvent.categories[0];
  }, [raceEvent, categoryId]);

  // Build a flat view for child components by merging RaceEvent + active category
  const raceData = useMemo(() => {
      if (!raceEvent || !activeCategory) return null;
      return {
          id: raceEvent.id,
          name: raceEvent.name,
          date: raceEvent.date,
          category: activeCategory.name,
          scheduledStartTime: activeCategory.scheduledStartTime,
          startTime: activeCategory.startTime,
          duration: activeCategory.duration,
          distance: activeCategory.distance,
          course: raceEvent.course,
          athletes: activeCategory.athletes,
          imageUrl: raceEvent.imageUrl,
          ownerId: raceEvent.ownerId,
          isExternal: raceEvent.isExternal,
          websiteUrl: raceEvent.websiteUrl,
          isCircuit: raceEvent.isCircuit,
          externalId: raceEvent.externalId,
          externalSource: raceEvent.externalSource,
          eventType: raceEvent.eventType,
      };
  }, [raceEvent, activeCategory]);

  const activeRaceData = useMemo(() => {
        if (!raceData) return null;
        
        let currentStartTime = raceData.startTime;
        let currentDuration = raceData.duration;
        let currentCourse = raceData.course;

        // Dynamic fallback for webhook-added athletes where startTime is still 0
        if (currentStartTime === 0 && raceData.athletes && raceData.athletes.length > 0) {
            const allStartTimestamps = raceData.athletes.flatMap(a => 
                (a.track && a.track.length > 0) ? a.track[0].timestamp : []
            );
            if (allStartTimestamps.length > 0) {
                currentStartTime = Math.min(...allStartTimestamps);

                // If duration is 0, estimate it
                if (currentDuration === 0) {
                    const allEndTimestamps = raceData.athletes.flatMap(a => 
                        (a.track && a.track.length > 0) ? a.track[a.track.length - 1].timestamp : []
                    );
                    const maxEnd = Math.max(...allEndTimestamps);
                    currentDuration = maxEnd > currentStartTime ? maxEnd - currentStartTime : 0;
                }
            }
        }

        // Dynamic fallback for missing course path
        if ((!currentCourse || !currentCourse.path || currentCourse.path.length === 0) && raceData.athletes && raceData.athletes.length > 0) {
            const firstValidTrack = raceData.athletes.find(a => a.track && a.track.length > 0)?.track;
            if (firstValidTrack && firstValidTrack.length > 0) {
                const startCoords: [number, number] = (currentCourse?.start && (currentCourse.start[0] !== 0 || currentCourse.start[1] !== 0))
                    ? [currentCourse.start[0], currentCourse.start[1]]
                    : [firstValidTrack[0].lat, firstValidTrack[0].lon];

                const fullPath: [number, number][] = firstValidTrack.map(p => [p.lat, p.lon]);
                const singleLoopPath = extractSingleCircuitLoop(fullPath, startCoords);
                
                currentCourse = {
                    path: singleLoopPath,
                    start: startCoords,
                    finish: (currentCourse?.finish && (currentCourse.finish[0] !== 0 || currentCourse.finish[1] !== 0)) ? currentCourse.finish : startCoords
                };
            }
        }

        // Group ride route trimming fallback for older untrimmed database courses
        if (raceData.eventType === 'group_ride' && currentCourse?.path && currentCourse.path.length > 0 && currentCourse.start && (currentCourse.start[0] !== 0 || currentCourse.start[1] !== 0)) {
            const distToStartMeters = getDistanceMeters(currentCourse.path[0], currentCourse.start);
            if (distToStartMeters > 1500) {
                const finishCoords: [number, number] = (currentCourse.finish && (currentCourse.finish[0] !== 0 || currentCourse.finish[1] !== 0))
                    ? currentCourse.finish
                    : currentCourse.start;
                const trimmed = trimRouteSegment(currentCourse.path, currentCourse.start, finishCoords);
                if (trimmed && trimmed.trimmedPath.length > 5) {
                    currentCourse = {
                        ...currentCourse,
                        path: trimmed.trimmedPath,
                    };
                }
            }
        }

        if (currentStartTime === 0) return { ...raceData, course: currentCourse }; // Still 0, no data yet

        let maxTimestamp = currentStartTime;
        let hasData = false;
        
        // Determine the end boundary. Add a generous buffer to the scheduled duration to ensure all finish times are included.
        // The actual intelligent trimming is now handled during file upload.
        const GRACE_BUFFER_MS = 30 * 60 * 1000; // 30 mins
        // Group ride durations are estimates (distance / pace), so never trim riders' tracks by them
        const isGroupRideEvent = raceData.eventType === 'group_ride';
        const endTime = (currentDuration > 0 && !isGroupRideEvent) ? currentStartTime + currentDuration + GRACE_BUFFER_MS : Infinity;

        const filteredAthletes = raceData.athletes.map(athlete => {
            if (!athlete.track || athlete.track.length === 0) return athlete;
            
            const filteredTrack = athlete.track.filter(p => p.timestamp >= currentStartTime && p.timestamp <= endTime);
            
            if (filteredTrack.length > 0) {
                const lastPt = filteredTrack[filteredTrack.length - 1];
                if (lastPt.timestamp > maxTimestamp) maxTimestamp = lastPt.timestamp;
                hasData = true;
            }
            
            return { ...athlete, track: filteredTrack };
        });
        
        // The playback slider duration should be driven by the actual last rider's finish time, not just the scheduled time.
        const activePlaybackDuration = hasData ? maxTimestamp - currentStartTime : currentDuration;

        return {
            ...raceData,
            startTime: currentStartTime,
            athletes: filteredAthletes,
            duration: activePlaybackDuration,
            course: currentCourse
        };
    }, [raceData]);

  // ASYNC ANALYSIS: Fetch Dynamic Offsets from Cloud
  useEffect(() => {
      if (!activeRaceData || activeRaceData.athletes.length === 0 || !raceId) return;

      const runAnalysis = async () => {
          setIsAnalyzing(true);
          try {
              // Call protected backend function
              const result = await cloudAnalysisService.getRaceAnalysis(activeRaceData, raceId);
              setDynamicOffsets(result.offsets);
              setIncidents(result.incidents);
          } catch (e) {
              console.error("Analysis Error", e);
          } finally {
              setIsAnalyzing(false);
          }
      };

      runAnalysis();
  }, [activeRaceData, raceId]); // Re-run when race data changes (e.g. upload)


  const failedLoads = useMemo(() => {
      if (!activeRaceData) return [];
      return activeRaceData.athletes.filter(a => !a.track || a.track.length === 0);
  }, [activeRaceData]);

  const existingUserAthlete = useMemo(() => {
      if (!activeRaceData || !currentUser) return null;
      return activeRaceData.athletes.find(a => a.userId === currentUser.uid || a.id === currentUser.uid);
  }, [activeRaceData, currentUser]);

  const { currentTime, isPlaying, playbackSpeed, play, pause, seek, setSpeed } = usePlayback({
    totalDuration: activeRaceData?.duration ?? 0,
  });

  const handleFileUpload = useCallback(async (file: File) => {
    setUploadError(null);
    if (activeRaceData && raceEvent && activeCategory) {
      pause();
      try {
        let raceDataForUpload = { ...activeRaceData };

        if (existingUserAthlete && !isAdmin) {
            const filteredAthletes = activeRaceData.athletes.filter(a => a.userId !== currentUser?.uid && a.id !== currentUser?.uid);
            raceDataForUpload = {
                ...activeRaceData,
                athletes: filteredAthletes
            };
        }

        let displayAthleteName: string | undefined = undefined;
        if (userProfile && userProfile.firstName) {
            displayAthleteName = `${userProfile.firstName} ${userProfile.lastName}`.trim();
        } else if (currentUser?.displayName) {
            displayAthleteName = currentUser.displayName;
        }

        const profileDemographics = userProfile ? {
            weightKg: userProfile.weightLbs ? userProfile.weightLbs / 2.20462 : undefined,
            heightCm: (userProfile.heightFeet || userProfile.heightInches)
                ? ((userProfile.heightFeet || 0) * 12 + (userProfile.heightInches || 0)) * 2.54
                : undefined,
            bikeWeightKg: userProfile.bikeWeightLbs ? userProfile.bikeWeightLbs / 2.20462 : undefined
        } : undefined;

        const updatedRaceData = await parseAndAddAthlete(
            raceDataForUpload, 
            file, 
            currentUser?.uid, 
            displayAthleteName,
            undefined,
            profileDemographics
        );

        const updatedCategories = raceEvent.categories.map((cat: RaceCategory) =>
            cat.id === activeCategory.id ? { 
                ...cat, 
                athletes: updatedRaceData.athletes,
                startTime: updatedRaceData.startTime,
                duration: updatedRaceData.duration
            } : cat
        );
        const updatedEvent: RaceEvent = { 
            ...raceEvent, 
            course: updatedRaceData.course || raceEvent.course,
            categories: updatedCategories 
        };
        setRaceEvent(updatedEvent);
        await raceEventService.updateCategoryData(raceEvent.id, activeCategory.id, {
            athletes: updatedRaceData.athletes,
            startTime: updatedRaceData.startTime,
            duration: updatedRaceData.duration
        });

        // Refresh from server to sync any backend-side course updates
        setTimeout(async () => {
             if (raceId) {
                 const freshEvent = await raceEventService.getRaceEventBySlug(raceId);
                 if (freshEvent) setRaceEvent(freshEvent);
             }
        }, 2500);
      } catch (error) {
        console.error("Error adding athlete:", error);
        const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred.';
        setUploadError(errorMessage);
      }
    }
  }, [activeRaceData, raceEvent, activeCategory, pause, currentUser, existingUserAthlete, isAdmin, userProfile]);
  
  const handleDeleteAthlete = (athleteId: string) => {
      setAthleteToDeleteId(athleteId);
  };

  const confirmDeleteAthlete = async () => {
      if (!activeRaceData || !raceEvent || !activeCategory || !athleteToDeleteId) return;

      const updatedAthletes = activeRaceData.athletes.filter(a => a.id !== athleteToDeleteId);

      // If no athletes left, clear course path immediately in local state but keep start/finish
      const updatedCourse = updatedAthletes.length === 0 && raceEvent.course
          ? { ...raceEvent.course, path: [] }
          : raceEvent.course;

      const updatedCategories = raceEvent.categories.map((cat: RaceCategory) =>
          cat.id === activeCategory.id ? { ...cat, athletes: updatedAthletes } : cat
      );
      const updatedEvent: RaceEvent = { ...raceEvent, course: updatedCourse, categories: updatedCategories };

      setRaceEvent(updatedEvent);

      try {
          // Use the secure Cloud Function to handle the deletion instead of a direct client write
          const functions = getFunctions();
          const deleteAthleteFn = httpsCallable(functions, 'deleteAthleteFromRace');
          await deleteAthleteFn({ 
              raceId: raceEvent.id, 
              categoryId: activeCategory.id, 
              athleteId: athleteToDeleteId 
          });

          // Refresh from server to sync any backend-side course updates
          setTimeout(async () => {
             if (raceId) {
                 const freshEvent = await raceEventService.getRaceEventBySlug(raceId);
                 if (freshEvent) setRaceEvent(freshEvent);
             }
          }, 2500);
      } catch (e) {
          console.error("Failed to delete athlete", e);
          setUploadError("Failed to remove file from server.");
      }
      setAthleteToDeleteId(null);
  };

  const handleSpotlightToggle = (athleteId: string) => {
      setSpotlightedAthleteId(prevId => prevId === athleteId ? null : athleteId);
  };

  const handleViewProfile = (athleteId: string) => {
    setInsightAthleteId(null);
    setViewedAthleteId(athleteId);
  };

  const handleViewInsights = (athleteId: string) => {
    setViewedAthleteId(null);
    setInsightAthleteId(athleteId);
  };

  // ASYNC ANALYSIS: Fetch Race Laps from Cloud
  useEffect(() => {
      if (!activeRaceData || !activeRaceData.course) {
          setRaceLapsData({});
          return;
      }

      const fetchRaceStats = async () => {
          const stats = await cloudAnalysisService.getRaceStats(activeRaceData);
          setRaceLapsData(stats.lapCrossings);
      };
      fetchRaceStats();
  }, [activeRaceData]);

  // Fetch Official Results (races only; group rides use Race Sector results instead)
  useEffect(() => {
      console.debug('[OfficialResults] activeCategory:', activeCategory?.id, 'externalId:', activeCategory?.externalId);
      if (raceEvent?.eventType === 'group_ride' || !activeCategory?.externalId) {
          console.debug('[OfficialResults] No externalId on category — auto-linker may not have run yet.');
          setOfficialResults([]);
          return;
      }

      const fetchResults = async () => {
          setIsLoadingResults(true);
          try {
              console.debug('[OfficialResults] Calling getAndCacheOfficialResults for', raceEvent?.id, activeCategory.id);
              const results = await bikedataService.getResults(raceEvent?.id || '', activeCategory.id);
              console.debug('[OfficialResults] Raw results returned:', results?.length, results);
              
              // Fetch user profiles to check for USAC License Numbers
              const userIds = activeCategory.athletes.map(a => a.userId).filter(Boolean) as string[];
              const uniqueUserIds = [...new Set(userIds)];
              
              const profilePromises = uniqueUserIds.map(uid => 
                  userService.getUserProfile(uid).catch(() => null)
              );
              const userProfiles = await Promise.all(profilePromises);
              
              const profileMap = new Map<string, any>();
              uniqueUserIds.forEach((uid, index) => {
                  if (userProfiles[index]) profileMap.set(uid, userProfiles[index]);
              });

              // Local matching logic to link telemetry with official results
              const matchedResults = results.map(result => {
                  const match = activeCategory.athletes.find(a => {
                      // 1. Primary: USAC License Match
                      if (a.userId && result.usacLicenseNumber) {
                          const profile = profileMap.get(a.userId);
                          if (profile && profile.usacLicenseNumber === result.usacLicenseNumber) return true;
                      }
                      // 2. Fallback: Fuzzy Name Match
                      return fuzzyMatchName(a.name, result.name);
                  });
                  return match ? { ...result, isMatched: true, matchedAthleteId: match.id } : result;
              });
              
              console.debug('[OfficialResults] Matched results:', matchedResults?.length);
              setOfficialResults(matchedResults);
          } catch (e) {
              console.error("Failed to fetch official results", e);
              setOfficialResults([]);
          } finally {
              setIsLoadingResults(false);
          }
      };

      fetchResults();
  }, [activeCategory, raceEvent?.id, raceEvent?.eventType]);

  // Pre-calculate course geometry metrics
  const courseMetrics = useMemo(() => {
      if (!activeRaceData || !activeRaceData.course || activeRaceData.course.path.length === 0) return { length: 0, cumulative: [] };
      return {
          length: calculateCourseLength(activeRaceData.course.path),
          cumulative: calculateCourseCumulativeDistances(activeRaceData.course.path)
      };
  }, [activeRaceData]);

  const athletePerformanceData = useMemo(() => {
    if (!activeRaceData || !activeRaceData.athletes || activeRaceData.startTime === 0) return [];
    
    const absoluteTime = activeRaceData.startTime + currentTime;
    const courseLen = courseMetrics.length;
    if (courseLen === 0) return [];

    const athletesWithState = activeRaceData.athletes.map(athlete => {
        if (!athlete.track || athlete.track.length === 0) {
          return {
            id: athlete.id, name: athlete.name, color: athlete.color,
            distance: 0, lapCount: 0, currentMetrics: { speed: 0, power: null, heartRate: null },
            position: { lat: activeRaceData.course.start[0], lon: activeRaceData.course.start[1] },
            virtualRaceDistance: 0,
            startTime: 0,
            startCoordinate: undefined,
            userId: athlete.userId
          };
        }
        const currentPoint = getCurrentTrackPointForAthlete(athlete.track, absoluteTime);
        
        // Use Async Dynamic Offsets from State
        const athleteAdjustments = dynamicOffsets.get(athlete.id) || [];
        let currentAdjustment = { lapOffset: 0, distanceOffset: 0 };
        for (let i = athleteAdjustments.length - 1; i >= 0; i--) {
            if (athleteAdjustments[i].timestamp <= absoluteTime) {
                currentAdjustment = athleteAdjustments[i];
                break;
            }
        }
        
        const rawLaps = getLapCountAtTime(athlete.track, absoluteTime, activeRaceData.course.start);
        const adjustedLaps = rawLaps + currentAdjustment.lapOffset;
        
        const totalRecordedDistance = currentPoint.distance + currentAdjustment.distanceOffset;
        const hintDist = totalRecordedDistance % courseLen;
        
        const projectedProgress = getProjectedPathDistance(
            currentPoint.lat, currentPoint.lon,
            activeRaceData.course.path,
            courseMetrics.cumulative,
            courseLen,
            hintDist
        );

        const virtualRaceDistance = calculateVirtualRaceDistance(
            adjustedLaps,
            projectedProgress,
            courseLen,
            totalRecordedDistance
        );
        
        const startCoordinate = athlete.track.length > 0 ? { lat: athlete.track[0].lat, lon: athlete.track[0].lon } : undefined;
        const startTime = athlete.track.length > 0 ? athlete.track[0].timestamp : undefined;
        const hasOfficialResult = officialResults.some(r => r.matchedAthleteId === athlete.id);

        return {
            id: athlete.id,
            name: athlete.name,
            color: athlete.color,
            distance: totalRecordedDistance,
            lapCount: adjustedLaps,
            currentMetrics: {
                speed: currentPoint.speed,
                power: currentPoint.power,
                heartRate: currentPoint.heartRate,
            },
            position: { lat: currentPoint.lat, lon: currentPoint.lon },
            virtualRaceDistance,
            startTime,
            startCoordinate,
            userId: athlete.userId,
            hasOfficialResult
        };
    });

    const sortedAthletes = [...athletesWithState].sort((a, b) => b.virtualRaceDistance - a.virtualRaceDistance);
    
    if (sortedAthletes.length === 0) {
        return [];
    }
    
    // --- CONTINUOUS DISTANCE LEAD LAP LOGIC ---
    // A racer is on the lead lap if their virtual distance gap behind the leader is less than 70% of a lap,
    // or if their lapCount is equal to or greater than the leader's.
    const onLeadLapIds = new Set<string>();
    if (sortedAthletes.length > 0) {
        const leader = sortedAthletes[0];
        onLeadLapIds.add(leader.id);
        
        const leadLapGapThreshold = courseLen > 0 ? (courseLen * 0.70) : 600; // 70% of single loop or 600m
        
        for (let i = 1; i < sortedAthletes.length; i++) {
            const current = sortedAthletes[i];
            const distanceBehindLeader = leader.virtualRaceDistance - current.virtualRaceDistance;
            if (current.lapCount >= leader.lapCount || distanceBehindLeader < leadLapGapThreshold) {
                onLeadLapIds.add(current.id);
            }
        }
    }
    // -----------------------------

    const leader = sortedAthletes[0];
    const leaderTrack = activeRaceData.athletes.find(a => a.id === leader.id)?.track;
    const leaderOffsets = dynamicOffsets.get(leader.id) || [];

    return athletesWithState.map(athlete => {
        const rank = sortedAthletes.findIndex(a => a.id === athlete.id) + 1;
        const isLeader = rank === 1;
        let lapsDown = 0;
        let timeBehind = 0;

        if (!isLeader && leaderTrack) {
            const isOnLeadLap = onLeadLapIds.has(athlete.id);

            if (!isOnLeadLap) {
                const distDiff = leader.virtualRaceDistance - athlete.virtualRaceDistance;
                // If not on lead lap, calculate how many laps down
                lapsDown = Math.floor(distDiff / courseLen);
                if (lapsDown < 1) lapsDown = 1; 
                lapsDown = courseLen > 0 ? Math.floor(distDiff / courseLen) : 0;
                if (lapsDown < 1 && courseLen > 0) lapsDown = 1; 
            } else {
                lapsDown = 0;
                
                const leaderCurrentPoint = getCurrentTrackPointForAthlete(leaderTrack, absoluteTime);
                const STOPPAGE_SPEED_THRESHOLD = 1.0; // 1 m/s

                // If leader is stopped, the time gap shouldn't grow with wall-clock time.
                // Use a distance-based estimate as a fallback.
                if (leaderCurrentPoint.speed < STOPPAGE_SPEED_THRESHOLD) {
                    const distDiff = leader.virtualRaceDistance - athlete.virtualRaceDistance;
                    // Use a fallback speed to estimate time gap from distance. 10 m/s (36kph) is a reasonable average.
                    timeBehind = distDiff > 0 ? (distDiff / 10) * 1000 : 0;
                } else {
                    // Calculate time gap relative to leader by finding when the leader was at the current athlete's distance
                    const scanLimitTimestamp = absoluteTime - 600000; // Don't search back more than 10 mins
                    const targetVirtualDist = athlete.virtualRaceDistance;
                    let matchedTime = null;
                    
                    let low = 0, high = leaderTrack.length - 1;
                    let startIndex = high;
                    while(low <= high) {
                        const mid = (low + high) >>> 1;
                        if (leaderTrack[mid].timestamp > absoluteTime) high = mid - 1;
                        else { startIndex = mid; low = mid + 1; }
                    }

                    for (let i = startIndex; i >= 0; i--) {
                        const pt = leaderTrack[i];
                        if (pt.timestamp < scanLimitTimestamp) break;

                        let ptOffset = { lapOffset: 0, distanceOffset: 0 };
                        for (let j = leaderOffsets.length - 1; j >= 0; j--) {
                            if (leaderOffsets[j].timestamp <= pt.timestamp) {
                                ptOffset = leaderOffsets[j];
                                break;
                            }
                        }
                        
                        const lRawLaps = getLapCountAtTime(leaderTrack, pt.timestamp, activeRaceData.course.start);
                        const lAdjLaps = lRawLaps + ptOffset.lapOffset;
                        const lTotalRecDist = pt.distance + ptOffset.distanceOffset;
                        const lHint = lTotalRecDist % courseLen;
                        const lProj = getProjectedPathDistance(pt.lat, pt.lon, activeRaceData.course.path, courseMetrics.cumulative, courseLen, lHint);
                        const lVirtualDist = calculateVirtualRaceDistance(lAdjLaps, lProj, courseLen, lTotalRecDist);

                        if (lVirtualDist <= targetVirtualDist) {
                            matchedTime = pt.timestamp;
                            break;
                        }
                    }
                    
                    if (matchedTime) {
                        timeBehind = absoluteTime - matchedTime;
                    } else {
                        // Fallback for when no time match is found (e.g. athlete is ahead of where leader has ever been)
                        const distDiff = leader.virtualRaceDistance - athlete.virtualRaceDistance;
                        if (distDiff > 50) { // Only show gap if significant
                            timeBehind = (distDiff / 10) * 1000; // Assume 10 m/s speed
                        } else {
                            timeBehind = 0;
                        }
                    }
                }
            }
        }

        return {
            ...athlete,
            rank,
            lapsDown,
            timeBehind: Math.max(0, timeBehind)
        } as AthletePerformance;
    });

  }, [activeRaceData, currentTime, dynamicOffsets, courseMetrics, officialResults]);

  const athletePositions = useMemo(() => {
    return athletePerformanceData.map(({ id, name, color, position }) => ({
      id, name, color, position,
    }));
  }, [athletePerformanceData]);

  const leadAthletePosition = useMemo(() => {
    const leadAthleteId = spotlightedAthleteId || athletePerformanceData[0]?.id;
    return athletePerformanceData.find(a => a.id === leadAthleteId)?.position || null;
  }, [athletePerformanceData, spotlightedAthleteId]);

  const viewedAthlete = useMemo(() => {
      return athletePerformanceData.find(a => a.id === viewedAthleteId) || null;
  }, [viewedAthleteId, athletePerformanceData]);

  // --- Group ride: Race Sector ---
  const isGroupRide = raceEvent?.eventType === 'group_ride';
  const raceSector = raceEvent?.groupRideDetails?.spiritedSegment;

  const normalizedSector = useMemo(() => {
      if (!isGroupRide || !raceSector) return undefined;
      return normalizeSector(raceSector, activeRaceData?.course?.path);
  }, [isGroupRide, raceSector, activeRaceData?.course?.path]);

  const sectorPath = useMemo(() => {
      if (!isGroupRide || !activeRaceData?.course) return undefined;
      const slice = getSectorPathSlice(activeRaceData.course.path, normalizedSector || raceSector);
      return slice.length > 1 ? slice : undefined;
  }, [isGroupRide, activeRaceData, normalizedSector, raceSector]);

  const sectorLeaderboard = useMemo(() => {
      if (!isGroupRide || !activeRaceData) return { rows: [], incomplete: [] };
      return computeSectorLeaderboard(activeRaceData.athletes, normalizedSector || raceSector, activeRaceData?.course?.path);
  }, [isGroupRide, activeRaceData, normalizedSector, raceSector]);

  const sectorDistanceMiles = useMemo(() => {
      if (!isGroupRide) return null;
      return computeSectorStats(activeRaceData?.course?.path, raceEvent?.groupRideDetails?.elevationProfile, normalizedSector || raceSector)?.distanceMiles ?? null;
  }, [isGroupRide, activeRaceData, raceEvent, normalizedSector, raceSector]);

  const insightAthleteRankInfo = useMemo(() => {
      if (!insightAthleteId) return undefined;
      const perf = athletePerformanceData.find(a => a.id === insightAthleteId);
      if (!perf) return undefined;
      return {
          rank: perf.rank,
          totalAthletes: athletePerformanceData.length,
          timeBehindMs: perf.timeBehind,
          lapsDown: perf.lapsDown,
      };
  }, [insightAthleteId, athletePerformanceData]);

  const insightAthleteSectorRow = useMemo(() => {
      if (!insightAthleteId) return undefined;
      return sectorLeaderboard.rows.find(r => r.athleteId === insightAthleteId);
  }, [insightAthleteId, sectorLeaderboard.rows]);


  if (!activeRaceData || !raceEvent || !activeCategory) {
    return (
        <div className="flex items-center justify-center h-full">
            <h2 className="text-2xl text-gray-400">Loading Race Data...</h2>
        </div>
    );
  }

  const hasAthletes = activeRaceData.athletes && activeRaceData.athletes.length > 0;
  // Lap tabs only make sense for circuits; regular races keep them as before
  const showLapTabs = !isGroupRide || activeRaceData.isCircuit;
  const visibleAnalysisTab = !showLapTabs && (activeAnalysisTab === 'laps' || activeAnalysisTab === 'raceLaps') ? 'chart' : activeAnalysisTab;
  const canShowGeneralUpload = isAdmin || !existingUserAthlete;

  return (
    <div className="flex flex-col bg-gray-900 text-gray-100">
        <SEO 
            title={`${raceEvent.name} - ${activeCategory.name} | RaceLineIQ`} 
            description={`Race analysis and playback for ${raceEvent.name} - ${activeCategory.name}`} 
            url={`${window.location.origin}/race/${raceEvent.slug || raceEvent.id}${activeCategory ? `/category/${activeCategory.slug || activeCategory.id}` : ''}`}
        />
        <Header race={raceEvent} activeCategory={activeCategory} />
        {!currentUser && (
            <div className="bg-gradient-to-r from-cyan-950/80 via-gray-800 to-cyan-950/80 border-b border-cyan-500/30 px-6 py-2.5 flex items-center justify-between text-xs">
                <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></span>
                    <span className="text-gray-300">
                        <strong className="text-white">Public Replay:</strong> Press play to watch the rider telemetry and group playback.
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    <span className="hidden sm:inline text-gray-400">Were you in this ride?</span>
                    <Link
                        to={`/auth?mode=signup&returnTo=${encodeURIComponent(window.location.pathname)}`}
                        className="px-3 py-1 bg-cyan-400 hover:bg-cyan-300 text-gray-950 font-bold rounded-md text-xs transition-colors shadow-sm"
                    >
                        Create Account to Upload
                    </Link>
                </div>
            </div>
        )}
        <main className="flex-grow p-4 flex gap-4">
            <div className="w-2/3 flex flex-col gap-4">
                <div className="h-[60vh] flex flex-col relative">
                    {failedLoads.length > 0 && (
                        <div className="bg-yellow-900/80 border border-yellow-700 text-yellow-200 px-4 py-2 rounded-lg mb-2 text-sm flex items-center justify-between shrink-0">
                            <span className="flex items-center gap-2">Warning: Failed to load data...</span>
                            <button onClick={() => window.location.reload()} className="underline hover:text-white font-medium ml-4">Reload</button>
                        </div>
                    )}
                    {isAnalyzing && (
                        <div className="absolute top-2 right-2 z-[1000] bg-cyan-900/80 text-cyan-200 px-3 py-1 rounded-full text-xs flex items-center gap-2 border border-cyan-500/50">
                             <svg className="animate-spin h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Analyzing Race Logic...
                        </div>
                    )}
                    
                    <div className="flex-grow rounded-lg overflow-hidden border border-gray-700">
                        {activeRaceData.course ? (
                            <RaceMap
                                course={activeRaceData.course}
                                athletes={activeRaceData.athletes}
                                athletePositions={athletePositions}
                                highlightedAthleteId={highlightedAthleteId}
                                spotlightedAthleteId={spotlightedAthleteId}
                                mapView={mapView}
                                leadAthletePosition={leadAthletePosition}
                                currentUserId={currentUser?.uid}
                                sectorPath={sectorPath}
                                spiritedSegment={normalizedSector || raceSector}
                            />
                        ) : (
                            <div className="flex items-center justify-center h-full bg-gray-800 text-gray-400">
                                <div className="text-center">
                                    <p className="text-lg font-semibold">No Course Data</p>
                                    <p className="text-sm">Upload a GPS file to generate the course map.</p>
                                </div>
                            </div>
                        )}
                    </div>
                     <div className="absolute top-2 left-2 z-[1000] bg-gray-800 bg-opacity-70 p-1 rounded-lg flex items-center gap-1">
                        <button onClick={() => setMapView('full')} className={`px-2 py-1 rounded-md text-xs flex items-center gap-1 ${mapView === 'full' ? 'bg-cyan-500 text-white' : 'text-gray-300'}`}><ArrowsPointingOutIcon className="w-4 h-4" /> Full</button>
                        <button onClick={() => setMapView('follow')} className={`px-2 py-1 rounded-md text-xs flex items-center gap-1 ${mapView === 'follow' ? 'bg-cyan-500 text-white' : 'text-gray-300'}`}><MapPinIcon className="w-4 h-4"/> Follow</button>
                    </div>
                </div>
                
                <div className="bg-gray-800 p-4 rounded-lg border border-gray-700 flex flex-col gap-2 min-h-[320px]">
                    <div className="flex-shrink-0">
                        <PlaybackControls
                            currentTime={currentTime}
                            duration={activeRaceData.duration}
                            isPlaying={isPlaying}
                            playbackSpeed={playbackSpeed}
                            onPlayPause={() => (isPlaying ? pause() : play())}
                            onSeek={seek}
                            onSpeedChange={setSpeed}
                        />
                    </div>
                     <div className="flex-grow min-h-0 flex flex-col">
                        <div className="flex-shrink-0 border-b border-gray-700 mb-2">
                            <nav className="-mb-px flex space-x-1 sm:space-x-2">
                                <button onClick={() => setActiveAnalysisTab('chart')} className={`whitespace-nowrap py-2 px-1 sm:px-2 text-sm font-medium ${visibleAnalysisTab === 'chart' ? 'border-b-2 border-cyan-500 text-cyan-400' : 'text-gray-400 hover:text-white'}`}>Athlete Rider Stats</button>
                                {showLapTabs && (
                                    <>
                                        <button onClick={() => setActiveAnalysisTab('laps')} className={`whitespace-nowrap py-2 px-1 sm:px-2 text-sm font-medium ${visibleAnalysisTab === 'laps' ? 'border-b-2 border-cyan-500 text-cyan-400' : 'text-gray-400 hover:text-white'}`}>Athlete Lap Stats</button>
                                        <button onClick={() => setActiveAnalysisTab('raceLaps')} className={`whitespace-nowrap py-2 px-1 sm:px-2 text-sm font-medium ${visibleAnalysisTab === 'raceLaps' ? 'border-b-2 border-cyan-500 text-cyan-400' : 'text-gray-400 hover:text-white'}`}>Race Lap Stats</button>
                                    </>
                                )}
                                <button onClick={() => setActiveAnalysisTab('aiInsights')} className={`whitespace-nowrap py-2 px-1 sm:px-2 text-sm font-medium ${visibleAnalysisTab === 'aiInsights' ? 'border-b-2 border-cyan-500 text-cyan-400' : 'text-gray-400 hover:text-white'}`}>AI Insights</button>
                            </nav>
                        </div>
                        <div className="flex-grow min-h-0">
                           {visibleAnalysisTab === 'chart' ? (
                                <div className="flex flex-col">
                                    <div className="h-[400px]">
                                        <DataChart
                                            athletes={activeRaceData.athletes}
                                            currentTime={currentTime}
                                            raceStartTime={activeRaceData.startTime}
                                            metric={chartMetric}
                                            onMetricChange={setChartMetric}
                                            highlightedAthleteId={highlightedAthleteId}
                                            xAxisMetric={xAxisMetric}
                                            onXAxisMetricChange={setXAxisMetric}
                                            showElevationProfile={showElevation}
                                            onShowElevationChange={setShowElevation}
                                            dynamicOffsets={dynamicOffsets}
                                            currentUserId={currentUser?.uid}
                                        />
                                    </div>
                                    <div className="flex-shrink-0 pt-2">
                                        <DraftingStats athletes={activeRaceData.athletes} raceId={activeRaceData.id} />
                                    </div>
                                </div>
                           ) : visibleAnalysisTab === 'laps' ? (
                                activeRaceData.course ? (
                                    <LapData 
                                        athletes={activeRaceData.athletes} 
                                        course={activeRaceData.course} 
                                        dynamicOffsets={dynamicOffsets}
                                        incidents={incidents}
                                        currentUserId={currentUser?.uid}
                                    />
                                ) : (
                                    <div className="p-8 text-center text-gray-400">No course data available for lap analysis.</div>
                                )
                           ) : visibleAnalysisTab === 'raceLaps' ? (
                               <RaceLapStats 
                                   raceLapsData={raceLapsData} 
                                   athletes={activeRaceData.athletes} 
                                   dynamicOffsets={dynamicOffsets}
                               />
                           ) : (
                               currentUser ? (
                                   <GeminiRaceInsights raceData={activeRaceData} raceLapsData={raceLapsData} />
                               ) : (
                                   <div className="p-8 text-center bg-gray-900/60 rounded-lg border border-gray-700/60 flex flex-col items-center justify-center space-y-3">
                                       <div className="w-10 h-10 rounded-full bg-cyan-500/20 text-cyan-400 flex items-center justify-center text-lg border border-cyan-500/30">
                                           ✨
                                       </div>
                                       <div>
                                           <h4 className="text-sm font-bold text-white mb-1">AI Race Insights</h4>
                                           <p className="text-xs text-gray-400 max-w-sm leading-relaxed">
                                               Sign in or create a free account to unlock automated race analysis, tactical moves of the day, and pacing insights.
                                           </p>
                                       </div>
                                       <Link
                                           to={`/auth?returnTo=${encodeURIComponent(window.location.pathname)}`}
                                           className="px-4 py-2 bg-cyan-500 hover:bg-cyan-400 text-gray-950 font-bold rounded-lg text-xs transition-colors shadow-sm"
                                       >
                                           Sign In to Unlock
                                       </Link>
                                   </div>
                               )
                           )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="w-1/3 flex flex-col bg-gray-800 p-4 rounded-lg border border-gray-700">
                {viewedAthleteId && viewedAthlete ? (
                     <AthleteProfile athlete={viewedAthlete} onBack={() => setViewedAthleteId(null)} />
                ) : insightAthleteId ? (
                    currentUser ? (
                        <GeminiAthleteInsights 
                            athleteId={insightAthleteId} 
                            raceData={activeRaceData} 
                            rankInfo={insightAthleteRankInfo}
                            onBack={() => setInsightAthleteId(null)} 
                            isGroupRide={isGroupRide}
                            groupRideDetails={raceEvent?.groupRideDetails}
                            raceSector={normalizedSector || raceSector}
                            sectorLeaderboardRow={insightAthleteSectorRow}
                            totalSectorContenders={sectorLeaderboard.rows.length}
                            sectorDistanceMiles={sectorDistanceMiles}
                            sectorElevationGainFt={computeSectorStats(activeRaceData?.course?.path, raceEvent?.groupRideDetails?.elevationProfile, normalizedSector || raceSector)?.elevationGainFt}
                        />
                    ) : (
                        <div className="p-6 text-center bg-gray-900/80 rounded-lg border border-gray-700 space-y-3">
                            <h4 className="text-sm font-bold text-white">Rider AI Insights</h4>
                            <p className="text-xs text-gray-400 leading-relaxed">
                                Sign in to generate personalized AI coaching and performance insights for this rider.
                            </p>
                            <div className="flex gap-2 justify-center pt-1">
                                <button
                                    onClick={() => setInsightAthleteId(null)}
                                    className="px-3 py-1.5 text-xs text-gray-400 hover:text-white border border-gray-700 rounded-md transition-colors"
                                >
                                    Back
                                </button>
                                <Link
                                    to={`/auth?returnTo=${encodeURIComponent(window.location.pathname)}`}
                                    className="px-3 py-1.5 bg-cyan-500 hover:bg-cyan-400 text-gray-950 font-bold rounded-md text-xs transition-colors"
                                >
                                    Sign In
                                </Link>
                            </div>
                        </div>
                    )
                ) : (
                    <>
                        <div className="mb-4 flex-shrink-0">
                            {canShowGeneralUpload ? (
                                !currentUser ? (
                                    <div className="p-3 bg-gradient-to-r from-cyan-950/50 to-gray-800 border border-cyan-500/30 rounded-lg flex items-center justify-between gap-3">
                                        <div>
                                            <div className="text-xs font-bold text-white">Were you on this ride?</div>
                                            <div className="text-[11px] text-gray-300">Upload your GPS file to see your stats in the replay.</div>
                                        </div>
                                        <Link
                                            to={`/auth?mode=signup&returnTo=${encodeURIComponent(window.location.pathname)}`}
                                            className="px-3 py-1.5 bg-cyan-500 hover:bg-cyan-400 text-gray-950 font-bold rounded-md text-xs whitespace-nowrap transition-colors shadow-sm"
                                        >
                                            Upload Ride
                                        </Link>
                                    </div>
                                ) : (
                                    (activeRaceData.athletes.length < 10 || isAdmin) && (
                                        <FileUpload onFileUpload={handleFileUpload} error={uploadError} />
                                    )
                                )
                            ) : existingUserAthlete ? (
                                <div className="bg-gray-700/50 p-3 rounded-lg border border-gray-600">
                                    <h4 className="text-sm font-semibold text-cyan-400 mb-2">My Entry</h4>
                                    <div className="flex items-center gap-2">
                                        <div className="flex-grow">
                                            <FileUpload 
                                                onFileUpload={handleFileUpload} 
                                                error={uploadError} 
                                                label="Replace File" 
                                            />
                                        </div>
                                        <button 
                                            onClick={() => handleDeleteAthlete(existingUserAthlete.id)}
                                            className="p-2 bg-red-900/30 text-red-400 rounded-md border border-red-900/50 hover:bg-red-900/50 transition-colors"
                                            title="Delete my entry"
                                        >
                                            <TrashIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            ) : null}
                        </div>

                        <div className="flex border-b border-gray-700 mb-4 flex-shrink-0">
                            <button 
                                onClick={() => setActiveSidebarTab('telemetry')}
                                className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${activeSidebarTab === 'telemetry' ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-gray-400 hover:text-gray-300'}`}
                            >
                            Athlete Data
                            </button>
                            <button 
                                onClick={() => setActiveSidebarTab('official')}
                                className={`flex-1 py-2 text-sm font-medium border-b-2 transition-colors ${activeSidebarTab === 'official' ? 'border-cyan-500 text-cyan-400' : 'border-transparent text-gray-400 hover:text-gray-300'}`}
                            >
                                {isGroupRide ? '⚡ Race Sector' : 'Official Results'}
                            </button>
                        </div>

                        {activeSidebarTab === 'telemetry' ? (
                            hasAthletes ? (
                                <AthleteList 
                                    athletes={athletePerformanceData} 
                                    onHover={setHighlightedAthleteId}
                                    onViewProfile={handleViewProfile}
                                    onSpotlight={handleSpotlightToggle}
                                    spotlightedAthleteId={spotlightedAthleteId}
                                    onGetInsights={handleViewInsights}
                                    currentUserId={currentUser?.uid}
                                    onDelete={handleDeleteAthlete}
                                    hasRaceSector={isGroupRide && !!(normalizedSector || raceSector)}
                                />
                            ) : (
                                 <div className="flex-grow flex flex-col items-center justify-center text-gray-500 p-6 text-center border border-dashed border-gray-700 rounded-lg">
                                    <h3 className="text-lg font-semibold text-gray-300">No Telemetry Yet</h3>
                                    <p className="text-sm text-gray-500 mt-2 mb-4">Be the first to upload a data file!</p>
                                </div>
                            )
                        ) : isGroupRide ? (
                            <RaceSectorResults
                                hasSector={!!raceSector}
                                rows={sectorLeaderboard.rows}
                                incomplete={sectorLeaderboard.incomplete}
                                sectorDistanceMiles={sectorDistanceMiles}
                                onRowClick={handleSpotlightToggle}
                                spotlightedAthleteId={spotlightedAthleteId}
                                currentUserId={currentUser?.uid}
                            />
                        ) : isLoadingResults ? (
                            <div className="flex-grow flex flex-col items-center justify-center text-gray-500 p-6 text-center border border-dashed border-gray-700 rounded-lg">
                                <svg className="animate-spin h-8 w-8 text-cyan-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                <p className="text-sm font-medium">Fetching official results...</p>
                            </div>
                        ) : (
                             <OfficialResults 
                                 results={officialResults} 
                                 onRowClick={handleSpotlightToggle} 
                                 spotlightedAthleteId={spotlightedAthleteId}
                                 noExternalLink={!activeCategory?.externalId}
                             />
                        )}
                    </>
                )}
            </div>
        </main>
        
        <ConfirmationModal 
            isOpen={!!athleteToDeleteId}
            onClose={() => setAthleteToDeleteId(null)}
            onConfirm={confirmDeleteAthlete}
            title="Delete File?"
            message={`Are you sure you want to remove your file from this ${isGroupRide ? 'ride' : 'race'}? This action cannot be undone.`}
            confirmButtonText="Yes, delete my file"
        />
    </div>
  );
};

export default RaceVisualizer;
